import React, { useMemo } from "react";
import { HiOutlineGlobe, HiOutlineCollection } from "react-icons/hi";

import { cn } from "../../ui";
import { MODULES } from "./modules";
import type { StatsRecord } from "./types";

type Props = {
  /** When set, hero renders in "scoped" mode (big title, no greeting). */
  title?: string;
  /** Sub-text under the title / greeting. */
  subtitle?: string;
  /** Eyebrow pill text (default "Live"). */
  eyebrow?: string;
  /** Personalised first name for greeting mode. Ignored when `title` is set. */
  firstName?: string | null;
  /** Stats source — sum-aggregates drive the two KPI cards. */
  stats: StatsRecord | null | undefined;
  /** Hide the Locations KPI (for per-entity pages scoped below country level). */
  hideLocations?: boolean;
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 5) return "Working late";
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  if (hour < 21) return "Good evening";
  return "Good night";
};

const formatDate = () =>
  new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

const compactNumber = (n: number | null | undefined) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n < 1_000) return n.toLocaleString();
  if (n < 10_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  if (n < 1_000_000) return `${Math.round(n / 1_000).toLocaleString()}K`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
};

const aggregate = (
  stats: StatsRecord | null | undefined,
  predicate: (category: string) => boolean,
) => {
  if (!stats) return null;
  let total = 0;
  let found = false;
  for (const m of MODULES) {
    if (!predicate(m.category)) continue;
    const v = stats[m.statKey];
    if (typeof v === "number" && Number.isFinite(v)) {
      total += v;
      found = true;
    }
  }
  return found ? total : null;
};

const SummaryCard: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  tone?: "brand" | "neutral";
}> = ({ label, value, icon, tone = "neutral" }) => (
  <div
    className={cn(
      "flex items-center gap-3 rounded-2xl border px-4 py-3.5 transition-colors",
      tone === "brand"
        ? "border-brand/20 bg-brand/[0.04]"
        : "border-gray-200 bg-white",
    )}
  >
    <span
      className={cn(
        "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
        tone === "brand"
          ? "bg-brand text-white"
          : "bg-brand/10 text-brand ring-1 ring-inset ring-brand/15",
      )}
      aria-hidden
    >
      {icon}
    </span>
    <div className="min-w-0">
      <p className="truncate text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500">
        {label}
      </p>
      <p className="mt-0.5 truncate text-[20px] font-semibold tabular-nums leading-none tracking-tight text-gray-900">
        {value}
      </p>
    </div>
  </div>
);

const DashboardHero: React.FC<Props> = ({
  title,
  subtitle,
  eyebrow,
  firstName,
  stats,
  hideLocations = false,
}) => {
  const locations = useMemo(
    () => aggregate(stats, (c) => c === "geography"),
    [stats],
  );
  const content = useMemo(
    () => aggregate(stats, (c) => c !== "geography"),
    [stats],
  );

  const isScoped = Boolean(title);
  const eyebrowText = eyebrow ?? (isScoped ? "Overview" : "Live");

  return (
    <section className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white">
      {/* Ambient brand wash, single-colour */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-20 right-32 h-40 w-40 rounded-full bg-brand/[0.07] blur-3xl"
      />

      <div
        className={cn(
          "relative grid gap-6 px-6 py-6 sm:px-8 sm:py-7 lg:items-center lg:gap-10",
          hideLocations
            ? "lg:grid-cols-[minmax(0,1fr)_auto]"
            : "lg:grid-cols-[1.1fr_1fr]",
        )}
      >
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/5 px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brand">
            <span aria-hidden className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand/60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand" />
            </span>
            {eyebrowText}
          </div>

          {isScoped ? (
            <h1 className="mt-4 truncate text-[28px] font-semibold leading-tight tracking-tight text-gray-900 sm:text-[32px]">
              {title}
            </h1>
          ) : (
            <h1 className="mt-4 text-[28px] font-semibold leading-tight tracking-tight text-gray-900 sm:text-[32px]">
              {getGreeting()}
              {firstName ? (
                <>
                  , <span className="text-brand">{firstName}</span>
                </>
              ) : null}
              .
            </h1>
          )}

          <p className="mt-2 text-[13.5px] text-gray-500">
            {isScoped
              ? subtitle ?? formatDate()
              : `${formatDate()} · ${subtitle ?? "here’s your platform at a glance."}`}
          </p>
        </div>

        <div
          className={
            hideLocations
              ? "flex"
              : "grid grid-cols-1 gap-3 sm:grid-cols-2"
          }
        >
          <SummaryCard
            tone="brand"
            label="Total content"
            value={compactNumber(content)}
            icon={<HiOutlineCollection className="h-5 w-5" />}
          />
          {hideLocations ? null : (
            <SummaryCard
              label="Locations"
              value={compactNumber(locations)}
              icon={<HiOutlineGlobe className="h-5 w-5" />}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default DashboardHero;
